import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface Supplier {
  id: string;
  name: string;
  cnpj: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  paymentTerms: string | null;
  avgLeadDays: number | null;
  notes: string | null;
  active: boolean;
  createdAt: string;
  /**
   * Saldo em aberto com o fornecedor. `null` quando o usuário não tem acesso
   * ao financeiro — a API omite o valor em vez de mandar zero, que diria
   * "não deve nada".
   */
  openPayableTotal: number | null;
  overduePayableCount: number | null;
}

export function useSuppliers() {
  const queryClient = useQueryClient();

  const suppliersQuery = useQuery<{ suppliers: Supplier[] }>({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const res = await api.get('/suppliers?includeInactive=true');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      cnpj?: string | null;
      phone?: string | null;
      email?: string | null;
      website?: string | null;
      paymentTerms?: string | null;
      avgLeadDays?: number | null;
      notes?: string | null;
    }) => {
      const res = await api.post('/suppliers', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      name?: string;
      cnpj?: string | null;
      phone?: string | null;
      email?: string | null;
      website?: string | null;
      paymentTerms?: string | null;
      avgLeadDays?: number | null;
      notes?: string | null;
      active?: boolean;
    }) => {
      const res = await api.patch(`/suppliers/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/suppliers/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });

  return {
    suppliers: suppliersQuery.data?.suppliers ?? [],
    isLoading: suppliersQuery.isLoading,
    error: suppliersQuery.error,
    isError: suppliersQuery.isError,
    refetch: suppliersQuery.refetch,
    create: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    remove: deleteMutation.mutateAsync,
    isRemoving: deleteMutation.isPending,
  };
}
